import { BookOpen, GraduationCap, Library, LogOut, Menu, ShieldCheck, UserRound, Users, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useAcademy } from '../context/AcademyContext'
import { useAuth } from '../context/AuthContext'

const publicLinks = [
  { to: '/cursos', label: 'Cursos' },
  { to: '/#comunidad', label: 'Comunidad' },
  { to: '/#docente', label: 'Docente' },
  { to: '/#preguntas', label: 'Preguntas' },
]

const memberLinks = [
  { to: '/mi-aprendizaje', label: 'Mi aprendizaje', icon: GraduationCap },
  { to: '/comunidad', label: 'Comunidad', icon: Users },
  { to: '/biblioteca', label: 'Biblioteca', icon: Library },
  { to: '/perfil', label: 'Mi perfil', icon: UserRound },
]

export function PublicLayout({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const { user, demoMode } = useAuth()
  return <div className="site-shell">
    <header className="public-header">
      <Link className="brand" to="/" aria-label="Academia IA y Salud Mental, inicio">
        <span className="brand-mark" aria-hidden="true">IA</span>
        <span><strong>IA y Salud Mental</strong><small>Academia profesional</small></span>
      </Link>
      <button className="icon-button menu-button" type="button" aria-label={open ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={open} onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
      <nav className={open ? 'public-nav open' : 'public-nav'} aria-label="Navegación principal">
        {publicLinks.map((item) => <Link key={item.to} to={item.to} onClick={() => setOpen(false)}>{item.label}</Link>)}
        <Link className="button button-small button-ghost" to={user ? '/mi-aprendizaje' : '/ingresar'}>{user ? 'Ir a mi panel' : 'Ingresar'}</Link>
      </nav>
    </header>
    <main id="contenido">{children}</main>
    <footer className="footer">
      <div><div className="brand footer-brand"><span className="brand-mark">IA</span><span><strong>IA y Salud Mental</strong><small>Formación con criterio clínico</small></span></div><p>La IA puede asistir. El juicio profesional, la confidencialidad y el vínculo humano no se delegan.</p></div>
      <div><strong>Academia</strong><Link to="/cursos">Cursos</Link><Link to="/comunidad/normas">Normas de comunidad</Link><Link to="/privacidad">Privacidad</Link></div>
      <div><strong>Contacto</strong><a href="mailto:marce@iaysaludmental.com">marce@iaysaludmental.com</a><a href="https://iaysaludmental.com">Sitio principal</a></div>
      <p className="footer-bottom">© 2026 Marcela Barría Cárdenas{demoMode ? ' · Contenido demostrativo del MVP' : ' · Todos los derechos reservados'}</p>
    </footer>
  </div>
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, logout, demoMode } = useAuth()
  const { loading, error, clearError } = useAcademy()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const onLogout = async () => { await logout(); navigate('/') }
  return <div className="app-shell">
    <aside className={open ? 'sidebar open' : 'sidebar'}>
      <div className="sidebar-head"><Link className="brand brand-light" to="/"><span className="brand-mark">IA</span><span><strong>Academia</strong><small>IA y Salud Mental</small></span></Link><button className="icon-button sidebar-close" onClick={() => setOpen(false)} aria-label="Cerrar navegación"><X /></button></div>
      <nav aria-label="Área privada">
        {memberLinks.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to} onClick={() => setOpen(false)}><Icon aria-hidden="true" />{label}</NavLink>)}
        {user?.role === 'admin' && <NavLink to="/administracion" onClick={() => setOpen(false)}><ShieldCheck aria-hidden="true" />Administración</NavLink>}
      </nav>
      <div className="sidebar-user"><span className="avatar">{user?.name.charAt(0)}</span><span><strong>{user?.name}</strong><small>{user?.role === 'admin' ? 'Administradora' : 'Participante'}</small></span><button className="icon-button" aria-label="Cerrar sesión" onClick={onLogout}><LogOut /></button></div>
    </aside>
    <div className="app-main">
      <header className="app-topbar"><button className="icon-button app-menu" onClick={() => setOpen(true)} aria-label="Abrir navegación"><Menu /></button><div><BookOpen aria-hidden="true" /><span>Academia profesional</span></div>{loading && <span className="demo-badge">Actualizando…</span>}{demoMode && <span className="demo-badge">Modo demostración</span>}</header>
      <main id="contenido" className="app-content">{error && <div className="form-message error app-error" role="alert"><span>{error}</span><button type="button" onClick={clearError}>Cerrar</button></div>}{children}</main>
    </div>
  </div>
}
